import React from "react";
import { Box, Typography, TextField, useTheme } from "@mui/material";
import HtmlRenderer from "./HtmlRenderer";

/* ---------- helper fns ---------- */
const parenNumRx = /\(\s*\d+\s*\)/g;
const underscoreRx = /_{2,}/g;

// Only clean parenthetical numbers that don't match the questionId
const cleanText = (txt = "", questionId = null) => {
  if (!txt) return "";
  
  // If there's a questionId, only remove parentheses that don't match it
  if (questionId != null) {
    const expectedPattern = new RegExp(`\\(\\s*${questionId}\\s*\\)`, 'g');
    // Keep the expected pattern, remove others
    return txt.replace(/\(\s*\d+\s*\)/g, (match) => {
      return match.match(expectedPattern) ? match : '';
    }).trim();
  }
  
  // If no questionId, remove all parenthetical numbers
  return txt.replace(parenNumRx, "").trim();
};

const splitForBlanks = (str, hasBlank) => {
  if (underscoreRx.test(str)) {
    underscoreRx.lastIndex = 0;
    const parts = str.split(underscoreRx);
    return parts.map((p, i) => ({ text: p, blank: i < parts.length - 1 }));
  }
  return [{ text: str, blank: hasBlank }];
};

const measureBlank = (raw) => {
  const m = raw.match(/_{2,}/);
  if (!m) return 160;
  const len = m[0].length;
  return Math.min(Math.max(len * 8, 120), 280); // clamp
};

/* ---------- component ---------- */
export default function MockNoteCompletion({
  noteCompletionData,
  handleAnswerChange,
  answers,
}) {
  const theme = useTheme();
  const maroon = "#9D2235";                   // used for heading + numbers
  const divider = theme.palette.divider;
  const bgCard  = theme.palette.background.paper;
  const txtMain = theme.palette.text.primary;

  const sections = noteCompletionData?.sections || [];

  return (
    <Box sx={{ 
      width: "100%",
      userSelect: "text", // Enable text selection
      "& *": { userSelect: "text" }, // Ensure all children are selectable
    }}>
      {sections.map((section, sIdx) => (
        <Box
          key={sIdx}
          sx={{
            my: 1,
            p: 3,
            border: `1px solid ${divider}`,
            borderRadius: 2,
            bgcolor: bgCard,
            userSelect: "text", // Enable text selection on container
            "& *": { userSelect: "text" }, // Ensure all children are selectable
          }}
        >
          {/* ----- section heading w/ underline ----- */}
          {section.heading && (
            <HtmlRenderer
              content={section.heading}
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                mb: 2,
                pb: 0.5,
                color: txtMain,
                borderBottom: `1px solid ${divider}`,
                userSelect: "text", // Enable text selection
                cursor: "text" // Show text cursor
              }}
            />
          )}

          {/* ----- notes ----- */}
    {section.notes?.map((note, nIdx) => {
  const qid = note?.questionId;
  const rawTxt = note?.text ?? "";
  const dispTxt = cleanText(rawTxt, qid);
  
  // Only show input fields for questions with valid questionId
  const hasValidQuestionId = Number.isInteger(qid) && qid > 0;
  
  if (!hasValidQuestionId) {
    // Show as plain text if no valid questionId
    return (
      <HtmlRenderer
        key={nIdx}
        content={dispTxt}
        component="div"
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          lineHeight: 1.8,
          mb: 1.5,
          userSelect: "text",
          cursor: "text"
        }}
      />
    );
  }

  // For questions WITH valid questionId, show number + input fields
  const segments = splitForBlanks(dispTxt, true); // Always split for questions with IDs
  const blankW = measureBlank(rawTxt);

  return (
    <Typography
      key={nIdx}
      component="div"
      variant="body1"
      data-question-id={qid}
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        lineHeight: 1.8,
        mb: 1.5,
        userSelect: "text",
        cursor: "text"
      }}
    >
      <Box component="span" sx={{ mr: 1, fontWeight: 700, color: maroon }}>
        ({qid})
      </Box>
      {segments.map((seg, i) => (
        <Box
          key={i}
          component="span"
          sx={{ 
            display: "inline-flex", 
            alignItems: "center",
            userSelect: "text",
            cursor: "text"
          }}
        >
          <HtmlRenderer
            content={seg.text}
            component="span"
            sx={{
              display: "inline",
              userSelect: "text",
              cursor: "text"
            }}
          />
          {seg.blank && (
            <TextField
              variant="outlined"
              size="small"
              value={answers?.[qid] || ""}
              onChange={(e) => handleAnswerChange?.(qid, e.target.value)}
              autoComplete="off"
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
              inputProps={{
                style: { 
                  textAlign: "center",
                  WebkitAppearance: "none",
                  WebkitTapHighlightColor: "transparent",
                },
                autoComplete: "off",
                inputMode: "text",
                onTouchStart: (e) => e.stopPropagation(),
                onTouchEnd: (e) => e.stopPropagation(),
              }}
              sx={{
                mx: 1,
                minWidth: blankW,
                maxWidth: blankW,
                touchAction: "manipulation",
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  fontWeight: 600,
                  px: 1,
                  touchAction: "manipulation",
                  "& fieldset": { borderColor: divider },
                  "&:hover fieldset": { borderColor: maroon },
                  "&.Mui-focused fieldset": { borderColor: maroon },
                },
                "& input": { 
                  textAlign: "center", 
                  color: maroon,
                  touchAction: "manipulation",
                  WebkitAppearance: "none",
                  WebkitTapHighlightColor: "transparent",
                  fontSize: "16px", // Prevents zoom on iOS
                },
              }}
            />
          )}
        </Box>
      ))}
    </Typography>
  );
})}

        </Box>
      ))}
    </Box>
  );
}
